import { supabase } from "@/auth/supabase-client";
import {
  ensureFreshSession,
  SUPABASE_EDGE_TIMEOUT_MS,
  SUPABASE_POSTGREST_TIMEOUT_MS,
  withSupabaseTimeout,
} from "@/auth/session-guard";
import { tierForScore } from "../tier";
import { breakdownForScore, mapSignalType, scoreSummary } from "../score";
import {
  RESTORE_LIVE_CALL_ENABLED,
  RestoreNotActivatedError,
  SignalActionForbiddenError,
} from "../types";
import { parseRestoreSignal } from "@shared/validation/signal-feedback";
import {
  parseCanonicalPractices,
  parsePracticeClassification,
  parsePracticeLensSummary,
} from "../practice-lens-ui";
import type {
  ActionActorType,
  ActionHistoryEntry,
  ActivationContext,
  ArchiveReason,
  AssignmentResult,
  AssignableUser,
  CanonicalPractice,
  FeedPage,
  FeedPageParams,
  FeedSignal,
  FoundSignalContact,
  FeedTab,
  LifecycleStatus,
  OwnershipState,
  PracticeClassification,
  PracticeKey,
  RestoredSignal,
  ScoreBreakdown,
  SignalActionsAdapter,
  SignalContent,
  SignalFeedAdapter,
  SignalType,
  SignalTypeFilter,
  SortValue,
  TabCounts,
  Tier,
  TriageCounts,
  VerificationStatus,
} from "../types";
import type { SignalRelationshipResult } from "@/components/RelationshipResults";

/**
 * Live adapters (W1) — bound to the deployed v08 Edge Functions.
 *
 * All reads go through get_signal_feed / get_signal_detail /
 * get_signal_action_history and all mutations through the ownership +
 * action-ledger functions (claim_signal, assign_signal,
 * archive_signal_with_reason, record_signal_action_event, toggle_signal_save,
 * export_signal_feed_csv). No PostgREST table writes and no supabase.rpc — every
 * mutation is an Edge Function invocation, so the client-write gate stays green
 * with zero suppressions. The only direct PostgREST use is a read of active
 * user_profiles for the assignee roster (reads are permitted).
 *
 * The recipient/on-demand actions (share → Magic Link, relationship check,
 * tariff scan, send asset) are intentionally not wired here; they land in W2
 * with the recipient flow and throw loudly if a live switch reaches them.
 */

/* ============================================================
   Edge Function invocation
   ============================================================ */

/** Invoke a JSON Edge Function and return its parsed body, or throw a useful error. */
async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke(name, { body }),
    `${name} request`,
    SUPABASE_EDGE_TIMEOUT_MS,
  );
  if (error) throw await invokeFailure(name, error);
  return data as T;
}

/** Invoke an Edge Function that returns raw text (CSV export) and return it as a string. */
async function invokeText(
  name: string,
  body: Record<string, unknown>,
): Promise<string> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke(name, { body }),
    `${name} request`,
    SUPABASE_EDGE_TIMEOUT_MS,
  );
  if (error) throw await invokeFailure(name, error);
  if (typeof data === "string") return data;
  if (data instanceof Blob) return await data.text();
  if (data instanceof ArrayBuffer) return new TextDecoder().decode(data);
  return typeof data === "object" ? JSON.stringify(data) : String(data);
}

/**
 * Turn an invoke failure into the most specific error type we can justify.
 *
 * A 403 becomes SignalActionForbiddenError so callers can distinguish "you may
 * not do this" from "that didn't work, retry" (R-2). The HTTP status is read off
 * `error.context` BEFORE messageFor consumes the body. Every other failure keeps
 * the previous generic shape.
 */
async function invokeFailure(name: string, error: unknown): Promise<Error> {
  const status = (error as { context?: { status?: number } }).context?.status;
  const detail = await messageFor(error);
  if (status === 403) return new SignalActionForbiddenError(name, detail);
  return new Error(`${name}: ${detail}`);
}

/** Best-effort extraction of the function's error message (from its JSON body). */
async function messageFor(error: unknown): Promise<string> {
  const ctx = (error as { context?: Response }).context;
  if (ctx && typeof ctx.json === "function") {
    try {
      const parsed = (await ctx.json()) as { error?: { message?: string } };
      if (parsed?.error?.message) return parsed.error.message;
    } catch {
      /* non-JSON error body — fall through to the generic message */
    }
  }
  return error instanceof Error ? error.message : String(error);
}

/* ============================================================
   Live response shapes (as returned by the v08 functions)
   ============================================================ */

interface LiveScoreBreakdown {
  signal_weight: number;
  capability_fit: number;
  recency: number;
  target_account_bonus: number;
}

interface LiveFeedRow {
  signal_id: string;
  signal_type: string | null;
  signal_source: string | null;
  lifecycle_status: string | null;
  /** Legacy stage cache. Still on the wire (backend lane owns its retirement);
   *  no longer read by the client — see canonical_stage_outcome below. */
  pursuit_stage?: "stage_outreach_made" | "stage_meeting_scheduled" | "stage_proposal" | null;
  /** Canonical stage/outcome projection. Present on get_signal_detail rows only. */
  canonical_stage_outcome?: { current_stage_key?: string | null } | null;
  found_contacts?: FoundSignalContact[];
  ownership_state: string | null;
  assigned_to: string | null;
  assigned_to_display_name: string | null;
  claimed_by: string | null;
  claimed_by_display_name: string | null;
  is_assignment_stale: boolean | null;
  archive_reason_v2: string | null;
  observed_at: string | null;
  first_observed_at: string | null;
  last_observed_at: string | null;
  deadline_at: string | null;
  confidence_level: number | null;
  related_organization_name: string | null;
  signal_url: string | null;
  feed_title: string | null;
  feed_summary: string | null;
  scf_score: number | null;
  tier: number | null;
  score_breakdown: LiveScoreBreakdown | null;
  is_saved: boolean | null;
  follow_up_due_at: string | null;
  /** Present only on get_signal_detail rows. */
  signal_content?: unknown;
  /** Present only on get_signal_detail rows (v13 activation projection). */
  activationContext?: unknown;
  /** Present only on get_signal_detail rows (v14): the canonical org id for the
   *  signal's account, resolved via v_organization_canonical_resolution. */
  canonical_organization_id?: string | null;
  /** Present only on get_signal_detail rows (2W practice lens). Parsed via
   *  parsePracticeClassification, which never trusts this without validating
   *  it first. */
  practice_classification?: unknown;
}

/** counts keys mirror the FeedTab set (+ a follow_ups_due_now triage extra). */
type LiveCounts = Record<FeedTab, number> & {
  follow_ups_due_now?: number;
  unclaimed_tier1?: number;
};

interface LivePage {
  limit: number;
  has_more: boolean;
  next_cursor: unknown | null;
}

interface LiveFeedEnvelope {
  data: {
    tab: string;
    rows: LiveFeedRow[];
    counts: LiveCounts;
    latest_signal_at?: string | null;
    latest_sweep_finished_at?: string | null;
    page: LivePage;
    /** Present only for a practice-filtered request. Parsed with
     *  parsePracticeLensSummary against the practice key the request sent. */
    practice_lens?: unknown;
  };
}

interface LiveActionEvent {
  signal_action_event_id: string;
  event_type: string;
  actor_type: string | null;
  actor_user_id: string | null;
  occurred_at: string;
  payload: Record<string, unknown> | null;
}

/** UI signal-type enum → get_signal_feed canonical `signal_type` argument. */
const TYPE_PARAM: Record<SignalType, string> = {
  rfp: "rfp",
  hiring: "hiring",
  m_and_a: "ma",
  transformation: "transformation",
  leadership: "leadership",
  market_intel: "market_intel",
  target_account: "target_account",
};

/* ============================================================
   Row → FeedSignal mapping
   ============================================================ */

function parseContent(raw: unknown): SignalContent {
  const obj = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const str = (k: string) => (typeof obj[k] === "string" ? (obj[k] as string) : "");
  return {
    summary: str("summary"),
    why_it_matters: str("why_it_matters"),
    recommended_action: str("recommended_action"),
    talk_track: str("talk_track"),
  };
}

function mapBreakdown(
  raw: LiveScoreBreakdown | null,
  scfScore: number,
): ScoreBreakdown {
  if (!raw) return breakdownForScore(scfScore, false);
  const b = {
    signalWeight: raw.signal_weight ?? 0,
    capabilityFit: raw.capability_fit ?? 0,
    recency: raw.recency ?? 0,
    targetAccountBonus: raw.target_account_bonus ?? 0,
  };
  return { ...b, summary: scoreSummary(b) };
}

/** Safely coerce the get_signal_detail v13 activation projection. Returns null
 *  when absent (feed rows / older payloads); an unrecognised relationship maps to
 *  null so the panel shows its honest empty state rather than implying warmth. */
function parseActivationContext(raw: unknown): ActivationContext | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const rel = o.relationship;
  const relationship =
    rel === "existing_client" || rel === "prior_relationship" || rel === "net_new"
      ? rel
      : null;
  const edObj =
    o.engagementDetail && typeof o.engagementDetail === "object"
      ? (o.engagementDetail as Record<string, unknown>)
      : {};
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
  const vt = o.valueTier;
  const valueTier = vt === "Tier 1" || vt === "Tier 2" || vt === "Tier 3" ? vt : null;
  return {
    relationship,
    engagementDetail: {
      activeCount: num(edObj.activeCount),
      practiceCount: num(edObj.practiceCount),
      dormantCount: num(edObj.dormantCount),
    },
    lastTouch: typeof o.lastTouch === "string" ? o.lastTouch : null,
    valueTier,
  };
}

function mapRow(row: LiveFeedRow): FeedSignal {
  const scfScore = row.scf_score ?? 0;
  const tier: Tier =
    row.tier === 1 || row.tier === 2 || row.tier === 3
      ? (row.tier as Tier)
      : tierForScore(scfScore);
  return {
    id: row.signal_id,
    scfScore,
    tier,
    company: row.related_organization_name ?? "Unknown company",
    headline: row.feed_title ?? row.feed_summary ?? "Untitled signal",
    // signal_content is present only on get_signal_detail rows; feed rows map to
    // empty content (the card renders headline/summary, not content).
    content: parseContent(row.signal_content),
    signalType: mapSignalType(row.signal_type),
    saved: row.is_saved ?? false,
    scoreBreakdown: mapBreakdown(row.score_breakdown, scfScore),
    source: row.signal_source ?? "Unknown source",
    practice: null,
    eventType: row.signal_type,
    confidence: row.confidence_level,
    geography: null,
    ownershipState: (row.ownership_state ?? "unclaimed") as OwnershipState,
    lifecycleStatus: (row.lifecycle_status ?? "active") as LifecycleStatus,
    canonicalStageKey:
      (row.canonical_stage_outcome?.current_stage_key as
        | FeedSignal["canonicalStageKey"]
        | undefined) ?? null,
    foundContacts: Array.isArray(row.found_contacts) ? row.found_contacts : [],
    claimedBy: row.claimed_by,
    claimedByName: row.claimed_by_display_name,
    assignedTo: row.assigned_to,
    assignedToName: row.assigned_to_display_name,
    // Not derivable from the feed row; admins still see the stale dot via
    // isAssignmentStale (canSeeStale = isAdmin || assignedByViewer).
    assignedByViewer: false,
    isAssignmentStale: row.is_assignment_stale ?? false,
    archiveReason: row.archive_reason_v2 as ArchiveReason | null,
    // E5 — verification status is not on the live projection yet.
    verification: "unverified" as VerificationStatus,
    followUpDueAt: row.follow_up_due_at ?? null,
    firstObservedAt: row.first_observed_at,
    lastObservedAt: row.last_observed_at ?? row.observed_at,
    deadlineAt: row.deadline_at,
    url: row.signal_url,
    // Detail-only (get_signal_detail v13); null on feed rows → panel empty state.
    activationContext: parseActivationContext(row.activationContext),
    // Detail-only (get_signal_detail v14); null on feed rows → Slice B surfaces
    // stay in their honest "unavailable" state until the detail hydrates.
    canonicalOrgId: row.canonical_organization_id ?? null,
    // practiceClassification is deliberately NOT set here. A feed row never
    // carries practice_classification at all (leaving the field genuinely
    // undefined is correct — see getFeedPage), while a detail row's strict
    // null-vs-absent distinction is applied by the caller (getSignal), which
    // is the only place that knows this row came from get_signal_detail.
  };
}

/**
 * Strict detail-only practice classification read. The accepted contract
 * reserves literal `null` for "unavailable"; an OMITTED practice_classification
 * on a get_signal_detail response is a malformed/stale projection, not a
 * legitimate unavailable read, so it is refused here rather than silently
 * mapped to null. Feed rows are never passed to this function.
 */
function classifyDetailRow(row: LiveFeedRow): PracticeClassification | null {
  if (!Object.prototype.hasOwnProperty.call(row, "practice_classification")) {
    throw new Error(
      "get_signal_detail response is missing practice_classification",
    );
  }
  return parsePracticeClassification(row.practice_classification);
}

/* ============================================================
   Feed adapter
   ============================================================ */

export class LiveSignalFeedAdapter implements SignalFeedAdapter {
  private feedBody(params: {
    tab: FeedTab;
    signalType: SignalTypeFilter;
    cursor?: string | null;
    limit: number;
    sort: SortValue;
    practiceKey?: PracticeKey | null;
  }): Record<string, unknown> {
    const body: Record<string, unknown> = {
      tab: params.tab,
      limit: params.limit,
      sort: params.sort,
    };
    if (params.signalType !== "all") body.signal_type = TYPE_PARAM[params.signalType];
    if (params.cursor) body.cursor = JSON.parse(params.cursor);
    // Omitted entirely for "All" — never sent as an empty string or "all".
    if (params.practiceKey) body.practice_key = params.practiceKey;
    return body;
  }

  async getFeedPage(params: FeedPageParams): Promise<FeedPage> {
    const res = await invoke<LiveFeedEnvelope>(
      "get_signal_feed",
      this.feedBody(params),
    );
    const { rows, counts, page } = res.data;
    const signals = (rows ?? []).map(mapRow);
    const nextCursor =
      page?.has_more && page.next_cursor != null
        ? JSON.stringify(page.next_cursor)
        : null;
    // counts respect the active type filter but stay firm-wide across
    // practices, so counts[tab] is the true total for the tab + type view
    // (drives "Show 5 more of {n}") — it is NEVER the practice-scoped total.
    const total = counts?.[params.tab] ?? signals.length;
    const practiceLens = params.practiceKey
      ? parsePracticeLensSummary(res.data.practice_lens, params.practiceKey)
      : null;
    return { signals, nextCursor, total, practiceLens };
  }

  async getTabCounts(): Promise<TabCounts> {
    // Tab pills are type-independent: read the unfiltered counts summary.
    const res = await invoke<LiveFeedEnvelope>("get_signal_feed", {
      tab: "all",
      limit: 1,
    });
    const c = res.data.counts;
    return {
      all: c.all,
      unclaimed: c.unclaimed,
      claimed_by_me: c.claimed_by_me,
      saved: c.saved,
      follow_up_due: c.follow_up_due,
      archived: c.archived,
    };
  }

  async getTriageCounts(): Promise<TriageCounts> {
    // One call serves both the nav badges and the sidebar's "Latest signal"
    // stamp. The counts block is tab-independent — bdse_get_signal_feed builds
    // every count from its `scoped` CTE, which does not read p_tab — so asking
    // for the "all" tab returns exactly the same numbers the unclaimed tab did,
    // including unclaimed_tier1.
    //
    // R-SB-1: the stamp comes from the FIRST ROW, not from latest_signal_at.
    // The RPC's latest_signal_at is greatest(max(created_at), max(observed_at))
    // over every non-suppressed signal — archived, won and passed included — so
    // it can headline a date that appears nowhere in the active feed.
    //
    // Asking for tab "all" sorted "most_recent" makes row[0] the honest answer,
    // and it is a true maximum rather than a page-local one: the RPC applies
    // `order by observed_at desc nulls last, signal_id desc` to its `eligible`
    // set — the full active population, filtered to
    // lifecycle_status not in (archived, expired, suppressed, passed, won) —
    // and only then applies the limit. The server therefore does the max over
    // the whole population; limit 1 just collects it.
    const res = await invoke<LiveFeedEnvelope>("get_signal_feed", {
      tab: "all",
      sort: "most_recent",
      limit: 1,
    });
    const c = res.data.counts;
    return {
      total: c.all,
      unclaimedTier1: c.unclaimed_tier1 ?? 0,
      followUpsDue: c.follow_up_due,
      claimedByMe: c.claimed_by_me,
      saved: c.saved,
      archived: c.archived,
      // Null when the active population is empty, or when every active row has
      // a null observed_at (they sort last) — the sidebar renders its honest
      // "None on record" for null rather than falling back to a wrong date.
      updatedAt: res.data.rows[0]?.observed_at ?? null,
      latestSweepFinishedAt: res.data.latest_sweep_finished_at ?? null,
    };
  }

  async getSignal(id: string): Promise<FeedSignal | null> {
    // get_signal_detail now projects follow_up_due_at (Codex, Phase F), so the
    // mapped row carries the modal's follow-up directly — no ledger derivation.
    try {
      const res = await invoke<{ data: { signal: LiveFeedRow } }>(
        "get_signal_detail",
        { signal_id: id },
      );
      const row = res.data?.signal;
      if (!row) return null;
      // Detail-only, strict: practice_classification must be an own property
      // of the detail row (throws if absent — see classifyDetailRow).
      return { ...mapRow(row), practiceClassification: classifyDetailRow(row) };
    } catch (err) {
      if (/not[ _]?found/i.test(String(err))) return null;
      throw err;
    }
  }

  async exportFeedCsv(params: {
    tab: FeedTab;
    signalType: SignalTypeFilter;
    viewerId: string;
    practiceKey: PracticeKey | null;
  }): Promise<{ url: string }> {
    const body: Record<string, unknown> = { tab: params.tab };
    if (params.signalType !== "all") body.signal_type = TYPE_PARAM[params.signalType];
    // Included only when a practice is selected — omitted entirely for "All".
    if (params.practiceKey) body.practice_key = params.practiceKey;
    const csv = await invokeText("export_signal_feed_csv", body);
    const suffixParts: string[] = [params.tab];
    if (params.signalType !== "all") suffixParts.push(params.signalType);
    if (params.practiceKey) suffixParts.push(params.practiceKey);
    return {
      url: triggerCsvDownload(csv, `signals-${suffixParts.join("-")}.csv`),
    };
  }

  async listPractices(): Promise<CanonicalPractice[]> {
    const res = await invoke<{ data: { practices: unknown } }>(
      "list_signal_practices",
      {},
    );
    return parseCanonicalPractices(res.data?.practices);
  }

  async listAssignableUsers(viewerId: string): Promise<AssignableUser[]> {
    // Direct read (no write) of the active roster; user_profiles is
    // client-readable and there is no list-users Edge Function.
    await ensureFreshSession();
    const { data, error } = await withSupabaseTimeout(
      supabase
        .from("user_profiles")
        .select("id, display_name, email, primary_practice, primary_sub_practice, title")
        .eq("is_active", true)
        .neq("id", viewerId)
        .order("display_name", { ascending: true, nullsFirst: false }),
      "Assignable users lookup",
      SUPABASE_POSTGREST_TIMEOUT_MS,
    );
    if (error) throw new Error(`listAssignableUsers: ${error.message}`);
    return (data ?? []).map((u) => ({
      id: u.id,
      name: u.display_name || u.email || "Teammate",
      email: u.email || "",
      practice: u.primary_practice || u.primary_sub_practice || null,
      title: u.title || null,
    }));
  }
}

/** Create a Blob URL for the CSV and trigger a browser download. */
function triggerCsvDownload(csv: string, filename: string): string {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  if (typeof document !== "undefined") {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
  return url;
}

/* ============================================================
   Actions adapter
   ============================================================ */

function mapActorType(raw: string | null): ActionActorType {
  const s = (raw ?? "").toLowerCase();
  if (s.includes("system")) return "system";
  if (s.includes("recipient") || s.includes("token")) return "token_recipient";
  return "human";
}

function actorFallback(type: ActionActorType): string {
  if (type === "system") return "Signal Engine";
  if (type === "token_recipient") return "Magic Link recipient";
  return "Teammate";
}

function eventDetail(e: LiveActionEvent): string | null {
  const p = e.payload ?? {};
  const str = (k: string) => (typeof p[k] === "string" ? (p[k] as string) : null);
  switch (e.event_type) {
    case "context_note":
    case "assign":
      return str("context_note");
    case "follow_up_set":
      return str("due_at");
    case "archive_with_reason":
      return str("archive_reason") ?? str("reason");
    case "asset_sent":
      return str("asset_id");
    // stage_regressed carries the walked-back-to stage event id; the modal reads
    // it to render "Walked back to …" honestly and to re-derive the current stage.
    case "stage_regressed":
      return str("to_stage");
    default:
      return null;
  }
}

function notImplemented(action: string): never {
  throw new Error(
    `Live signal action "${action}" lands in W2 (recipient / on-demand flow); ` +
      `it is not wired in the W1 internal-surface swap.`,
  );
}

export class LiveSignalActionsAdapter implements SignalActionsAdapter {
  async claim(signalId: string): Promise<void> {
    await invoke("claim_signal", { signal_id: signalId });
  }

  // Symmetric release. Awaits Codex's `unclaim_signal` Edge Function (mirror of
  // claim_signal: flips claimed → unclaimed and writes an `unclaim` ledger
  // event; `unclaim` must also be added to SIGNAL_ACTION_EVENT_TYPES). Until it
  // lands this invoke returns a 404 the mutation surfaces; the affordance itself
  // is exercised mock-first. Kept wired so the live path works the moment the
  // function ships — no further UI change needed.
  async unclaim(signalId: string): Promise<void> {
    await invoke("unclaim_signal", { signal_id: signalId });
  }

  async assign(
    signalId: string,
    assigneeId: string,
    contextNote?: string,
  ): Promise<AssignmentResult> {
    const body: Record<string, unknown> = {
      signal_id: signalId,
      assigned_to_user_id: assigneeId,
    };
    if (contextNote) body.context_note = contextNote;
    const res = await invoke<{
      data: {
        assignee: { display_name: string | null; email: string };
        notification: { status: AssignmentResult["notificationStatus"] };
      };
    }>("assign_signal", body);
    return {
      assigneeName: res.data.assignee.display_name || res.data.assignee.email,
      assigneeEmail: res.data.assignee.email,
      notificationStatus: res.data.notification.status,
    };
  }

  async archiveWithReason(
    signalId: string,
    reason: ArchiveReason,
  ): Promise<void> {
    await invoke("archive_signal_with_reason", {
      signal_id: signalId,
      archive_reason: reason,
    });
  }

  async setFollowUp(signalId: string, dueAt: string): Promise<void> {
    await invoke("record_signal_action_event", {
      signal_id: signalId,
      event_type: "follow_up_set",
      payload: { due_at: dueAt },
    });
  }

  async toggleSave(signalId: string): Promise<{ saved: boolean }> {
    const res = await invoke<{ data?: { saved?: boolean }; saved?: boolean }>(
      "toggle_signal_save",
      { signal_id: signalId },
    );
    return { saved: res?.data?.saved ?? res?.saved ?? false };
  }

  async restoreSignal(signalId: string): Promise<RestoredSignal> {
    // R-4 live-call gate. The restore-signal function still carries the
    // pre-fix authorization check, so the client refuses to issue the request
    // until QB confirms the corrected check is deployed on blue. Flip
    // RESTORE_LIVE_CALL_ENABLED to activate; nothing else here changes.
    if (!RESTORE_LIVE_CALL_ENABLED) throw new RestoreNotActivatedError();
    // Payload validated against the SAME schema the Edge Function enforces
    // (shared/validation/signal-feedback.ts), so a malformed id fails fast
    // client-side instead of round-tripping to a 400.
    const payload = parseRestoreSignal({ signal_id: signalId });
    const res = await invoke<{ data: { signal: RestoredSignal } }>(
      "restore-signal",
      payload,
    );
    return res.data.signal;
  }

  async getActionHistory(signalId: string): Promise<ActionHistoryEntry[]> {
    const res = await invoke<{ data: { events: LiveActionEvent[] } }>(
      "get_signal_action_history",
      { signal_id: signalId },
    );
    const events = res.data?.events ?? [];
    const names = await resolveActorNames(events);
    return events
      .map((e) => {
        const actorType = mapActorType(e.actor_type);
        return {
          id: e.signal_action_event_id,
          actorType,
          actorName:
            (e.actor_user_id && names[e.actor_user_id]) ||
            actorFallback(actorType),
          verb: e.event_type,
          detail: eventDetail(e),
          at: e.occurred_at,
        };
      })
      // The server returns newest-first; the Timeline reverses its input, so
      // hand it chronological (oldest-first) order to match the mock contract.
      .sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
  }

  async recordStage(
    signalId: string,
    eventType: "stage_outreach_made" | "stage_meeting_scheduled" | "stage_proposal" | "stage_regressed",
    options: { toStage?: "assigned" | "stage_outreach_made" | "stage_meeting_scheduled"; reason?: string } = {},
  ): Promise<void> {
    await invoke("record_stage_event", {
      signal_id: signalId,
      event_type: eventType,
      ...(options.toStage ? { to_stage: options.toStage } : {}),
      ...(options.reason ? { reason: options.reason } : {}),
    });
  }

  async recordOutcome(
    signalId: string,
    outcome: "won" | "passed" | "reopen",
    reason?: string,
  ): Promise<void> {
    await invoke("record_outcome", {
      signal_id: signalId,
      outcome,
      ...(reason ? { reason } : {}),
    });
  }

  async findContacts(
    signalId: string,
    options: { refresh?: boolean } = {},
  ): Promise<FoundSignalContact[]> {
    const res = await invoke<{ data: { contacts?: FoundSignalContact[] } }>("find_contacts_proxy", {
      signal_id: signalId,
      ...(options.refresh ? { refresh: true } : {}),
    });
    return res.data?.contacts ?? [];
  }

  async saveFoundContact(
    signalId: string,
    foundContactId: string,
  ): Promise<{ id: string; already_saved: boolean }> {
    const res = await invoke<{ data: { saved_contact: { id: string; already_saved: boolean } } }>(
      "find_contacts_proxy",
      { signal_id: signalId, action: "save", found_contact_id: foundContactId },
    );
    return res.data.saved_contact;
  }

  // Share = mint a magic link for an internal recipient (create_magic_link is
  // an admin function, so it goes through the authenticated invoke path).
  async share(signalId: string, recipientId: string): Promise<{ url: string }> {
    const res = await invoke<{ data: { token: string } }>("create_magic_link", {
      signal_id: signalId,
      recipient_user_id: recipientId,
    });
    const token = res.data?.token;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return { url: token ? `${origin}/s/${token}` : "" };
  }

  // On-demand research runs record a ledger event through the authenticated
  // recorder (record_signal_action_event accepts relationship_check_run /
  // tariff_scan_run — both in SIGNAL_ACTION_EVENT_TYPES). Same invoke path as
  // setFollowUp. The recorder is admin-only, so this succeeds for an admin /
  // operator viewer and returns 403 for a non-admin (surfaced as an honest
  // error). For relationship_check_run the response now carries { event, result }
  // — the grouped-by-owner relationships — which the caller renders below the
  // run buttons. (Tariff still records only the event, no verdict.)
  async runRelationshipCheck(signalId: string): Promise<SignalRelationshipResult> {
    const res = await invoke<{ data: { result: SignalRelationshipResult } }>(
      "record_signal_action_event",
      {
        signal_id: signalId,
        event_type: "relationship_check_run",
        payload: {},
      },
    );
    const result = res?.data?.result;
    if (!result) {
      throw new Error(
        "record_signal_action_event: missing relationship result payload",
      );
    }
    return result;
  }
  async runTariffScan(signalId: string): Promise<void> {
    await invoke("record_signal_action_event", {
      signal_id: signalId,
      event_type: "tariff_scan_run",
      payload: {},
    });
  }
  // sendAsset stays unwired (out of scope for this punch list).
  sendAsset(): Promise<void> {
    return notImplemented("sendAsset");
  }
}

/** Resolve distinct human actor ids to display names for the timeline. */
async function resolveActorNames(
  events: LiveActionEvent[],
): Promise<Record<string, string>> {
  const ids = [
    ...new Set(events.map((e) => e.actor_user_id).filter((v): v is string => !!v)),
  ];
  if (ids.length === 0) return {};
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase
      .from("user_profiles")
      .select("id, display_name, email")
      .in("id", ids),
    "Actor names lookup",
    SUPABASE_POSTGREST_TIMEOUT_MS,
  );
  if (error || !data) return {};
  const out: Record<string, string> = {};
  for (const u of data) out[u.id] = u.display_name || u.email || "Teammate";
  return out;
}
